import * as _ from 'lodash';
import { useEffect } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { window } from '../../../../../globals';
import { useAsyncValue } from '../../../../../hooks';
import { get } from '../../../../../redux/api';
import useOrganizationSettings from '../../../shared/useOrganizationSettings';
import type { NotificationsProjectSettings } from '../../../../../../shared/settings/project/notifications';
import type { MinimalUser } from '../../../../../../shared/user';

type Users = MinimalUser[];

/**
 * Fetches the users for the organization. This is used to populate the user dropdowns.
 */
const useFetchUsers = () => {
  const addToast = useAddToast();
  const { value: users, isLoading, errors, setPending, setResolve, setReject } = useAsyncValue<Users>(null);

  useEffect(() => {
    (async function () {
      try {
        setPending();
        const { data } = await get<Users>('/api/users/minimal');
        if (data) {
          setResolve(data);
        }
      } catch (error: unknown) {
        addToast({ type: 'error', content: `Failed to fetch users.` });
      }
    }());
  }, [addToast, setPending, setReject, setResolve]);

  return {
    users,
    isLoading,
    errors,
  };
};

/**
 * Single hook that fetches and manages the state of the notifications settings.
 * This hook is responsible for fetching the settings and users, and updating the settings.
 */
const useNotifications = (orgId: number) => {
  const { users, isLoading: isLoadingUsers, errors: userErrors } = useFetchUsers();
  const {
    isLoading: isLoadingSettings,
    settings,
    errors: notificationsErrors,
    onSaveSettings,
    isSaveButtonDisabled,
    onUpdateSettings,
  } = useOrganizationSettings<NotificationsProjectSettings>({
    endpoint: `/api/organizations/${orgId}/settings/projects/notifications`,
    settingsSection: 'project notifications',
  });
  const isLoading = isLoadingUsers || isLoadingSettings.fetch;

  const isEmailNotificationsEnabled =
    !isLoading &&
    !(
      _.isEmpty(settings?.notificationDefaultEmailScanUsers) &&
      settings?.notificationDefaultEmailScanUserType === 'custom'
    );

  return {
    settings,
    users,
    isSaving: isLoadingSettings.save,
    isLoading: isLoadingSettings.fetch,
    errors: [...(userErrors || []), ...(notificationsErrors || [])],
    onSaveSettings,
    isSaveButtonDisabled,
    isEmailNotificationsEnabled,
    onUpdateSettings,
    updateUsersToCurrent: () => onUpdateSettings({
      notificationDefaultEmailScanUserType: 'current',
      notificationDefaultEmailScanUsers: window.user?.id ? [window.user?.id] : [],
    }),
    updateUsersToAll: () => onUpdateSettings({ notificationDefaultEmailScanUserType: 'all' }),
    initializeCustomUsers: () => {
      if (_.isEmpty(settings?.notificationDefaultEmailScanUsers)) {
        // add the current user if the list is empty so the user modal doesn't show up empty
        onUpdateSettings({
          notificationDefaultEmailScanUserType: 'custom',
          notificationDefaultEmailScanUsers: [window.user?.id as number],
        });
      } else {
        onUpdateSettings({ notificationDefaultEmailScanUserType: 'custom' });
      }
    },
    updateUsersToCustom: (userIds: number[]) =>
      onUpdateSettings({ notificationDefaultEmailScanUserType: 'custom', notificationDefaultEmailScanUsers: userIds }),
    disableEmailNotifications: () =>
      onUpdateSettings({ notificationDefaultEmailScanUserType: 'custom', notificationDefaultEmailScanUsers: [] }),
  };
};

export default useNotifications;
